import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { config, type Locale } from '../config';
import { dictionaries, type StringKey } from './strings';

export type { Locale };

interface I18nValue {
  locale: Locale;
  setLocale(next: Locale): void;
  t(key: StringKey, params?: Record<string, string | number>): string;
}

const I18nContext = createContext<I18nValue | null>(null);
const STORAGE_KEY = `${config.storagePrefix}locale`;

function detectLocale(): Locale {
  try {
    const fromQuery = new URLSearchParams(globalThis.location?.search ?? '').get('lang');
    if (fromQuery === 'en' || fromQuery === 'zh-TW') return fromQuery;
    const stored = globalThis.sessionStorage?.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'zh-TW') return stored;
    const nav = globalThis.navigator?.language ?? '';
    if (nav.startsWith('zh')) return 'zh-TW';
    if (nav.startsWith('en')) return 'en';
  } catch {
    /* SSR / storage 停用 */
  }
  return config.defaultLocale;
}

/** `{name}` 佔位符替換。刻意不支援複數規則 —— 需要時再引入正式的 i18n 套件。 */
export function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(params, key) ? String(params[key]) : match,
  );
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      globalThis.sessionStorage?.setItem(STORAGE_KEY, next);
      if (globalThis.document) globalThis.document.documentElement.lang = next === 'zh-TW' ? 'zh-Hant-TW' : 'en';
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo<I18nValue>(() => {
    const dict = dictionaries[locale];
    return {
      locale,
      setLocale,
      t: (key, params) => interpolate(dict[key] ?? key, params),
    };
  }, [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n 必須放在 <I18nProvider> 之內');
  return ctx;
}
